/**
 * 🎨 Theme Provider
 *
 * React context for theme management with dark/light mode toggle.
 * Provides global theme state and toggle functionality.
 *
 * Usage:
 *
 * // In App.tsx or main.tsx
 * import { ThemeProvider } from '@/design-system/providers/ThemeProvider';
 *
 * <ThemeProvider defaultTheme="light">
 *   <App />
 * </ThemeProvider>
 *
 * // In any component
 * import { useTheme } from '@/design-system/providers/ThemeProvider';
 *
 * function MyComponent() {
 *   const { theme, setTheme, resolvedTheme } = useTheme();
 *   return <button onClick={() => setTheme('light')}>Switch to Light</button>;
 * }
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type ThemeName = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

function resolveTheme(theme: ThemeName): ResolvedTheme {
  if (theme !== "system") {
    return theme;
  }

  if (typeof window !== "undefined") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }

  return "light";
}

interface ThemeContextType {
  /** Current theme setting ('light', 'dark', or 'system') */
  theme: ThemeName;
  /** Update theme setting */
  setTheme: (theme: ThemeName) => void;
  /** Actual resolved theme after system preference is applied */
  resolvedTheme: ResolvedTheme;
  /** Toggle between light and dark */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  /** Default theme on first load. Defaults to 'light'. */
  defaultTheme?: ThemeName;
  /** localStorage key for persisting theme. Defaults to 'omnibase-theme' */
  storageKey?: string;
  /** Force dark mode regardless of stored preference. */
  forceDarkMode?: boolean;
  /** Disable localStorage reading/writing. Useful during module-by-module migration. */
  disableStorage?: boolean;
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "omnibase-theme",
  forceDarkMode = false,
  disableStorage = false,
}: ThemeProviderProps) {
  const canUseStorage =
    !disableStorage &&
    typeof window !== "undefined" &&
    "localStorage" in window;

  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (forceDarkMode) {
      return "dark";
    }
    if (!canUseStorage) {
      return defaultTheme;
    }
    try {
      const stored = window.localStorage.getItem(
        storageKey
      ) as ThemeName | null;
      if (stored && ["light", "dark", "system"].includes(stored)) {
        return stored;
      }
    } catch {
      return defaultTheme;
    }
    return defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(theme)
  );

  // Update resolved theme when theme or system preference changes
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);

    // Apply theme class to document
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolved);
    root.style.colorScheme = resolved;
  }, [theme]);

  // Listen for system theme changes when using 'system' mode
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const resolved = mediaQuery.matches ? "dark" : "light";
      setResolvedTheme(resolved);

      const root = window.document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(resolved);
      root.style.colorScheme = resolved;
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  useEffect(() => {
    if (!canUseStorage) {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey || !event.newValue) {
        return;
      }

      if (["light", "dark", "system"].includes(event.newValue)) {
        setThemeState(event.newValue as ThemeName);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [canUseStorage, storageKey]);

  const setTheme = (newTheme: ThemeName) => {
    if (forceDarkMode) {
      setThemeState("dark");
      return;
    }

    if (canUseStorage) {
      try {
        window.localStorage.setItem(storageKey, newTheme);
      } catch {
        // Ignore storage failures and still update in-memory theme state.
      }
    }

    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    const newTheme = resolvedTheme === "dark" ? "light" : "dark";
    setTheme(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    setTheme,
    resolvedTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 * Must be used within a ThemeProvider
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
